// src/components/resources/tools/generators/FakeChat/MessagingPreview.tsx

'use client'

import { cn } from '~/lib/utils'
import { useFakeChatState } from './FakeChatContext'
import type { ChatMessage, ChatParticipant } from './FakeChat.types'
import { useResolvedMessages } from './useResolvedMessages'
import { getTheme } from './accentThemes'

/**
 * Props for MessagingMessage component
 *
 * @interface MessagingMessageProps
 * @property {ChatMessage} message - The message to display.
 * @property {ChatParticipant} [participant] - The participant who sent the message.
 * @property {boolean} showAvatars - Whether to show the avatar.
 * @property {boolean} showTimestamps - Whether to show the timestamp.
 * @property {boolean} showReactions - Whether to show the reactions.
 */
interface MessagingMessageProps {
  message: ChatMessage
  participant?: ChatParticipant
  showAvatars: boolean
  showTimestamps: boolean
  showReactions: boolean
}

/**
 * Individual message component for messaging platform style
 */
function MessagingMessage({
  message,
  participant,
  showAvatars,
  showTimestamps,
  showReactions
}: MessagingMessageProps): JSX.Element {
  return (
    <div className="flex gap-3">
      {showAvatars ? (
        <div className="flex flex-col items-center">
          <span className={cn('flex h-9 w-9 items-center justify-center rounded-md text-sm font-semibold text-white', participant?.avatarColor ?? 'bg-slate-600')}>
            {participant?.avatarInitials ?? '?'}
          </span>
        </div>
      ) : null}
      <div className="flex-1 space-y-1">
        <div className="flex items-baseline gap-2">
          <p className="text-sm font-semibold text-slate-100">{participant?.displayName ?? 'Unknown sender'}</p>
          {participant?.handle ? (
            <span className="text-xs text-slate-400">{participant.handle}</span>
          ) : null}
          {showTimestamps ? (
            <span className="text-xs text-slate-500">{message.timestamp}</span>
          ) : null}
        </div>
        <p className="text-sm leading-relaxed text-slate-100/90">{message.text}</p>
        {message.attachments?.map(attachment => (
          <div
            key={attachment.id}
            className="mt-2 rounded-xl border border-slate-700/80 bg-slate-800/70 p-3 text-sm text-slate-200 shadow-inner"
          >
            <p className="font-medium">{attachment.title}</p>
            {attachment.description ? (
              <p className="mt-1 text-xs text-slate-400">{attachment.description}</p>
            ) : null}
          </div>
        ))}
        {showReactions && message.reactions.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {message.reactions.map(reaction => (
              <span
                key={reaction.id}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border border-slate-600/80 bg-slate-800/70 px-2 py-1 text-xs text-slate-200',
                  reaction.reactedByViewer ? 'border-indigo-400/60 bg-indigo-500/10 text-indigo-200' : ''
                )}
              >
                {reaction.emoji}
                <span>{reaction.count}</span>
              </span>
            ))}
          </div>
        ) : null}
        {message.isThreaded ? (
          <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-slate-700/80 px-3 py-1 text-xs text-slate-400">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-500/20 text-indigo-200">↪</span>
            <span>{message.threadReplyCount ?? 1} replies</span>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Messaging platform preview component
 * @returns {JSX.Element} The MessagingPreview component.
 */
export function MessagingPreview(): JSX.Element {
  const { conversation } = useFakeChatState()
  const theme = getTheme(conversation.metadata.accentColor)
  const resolvedMessages = useResolvedMessages(conversation.messages)
  // The shared hook keeps the participant joins identical across platforms so grouped
  // bubbles behave the same regardless of which preview is active.
  const channelLabel = conversation.metadata.channelType === 'channel'
    ? 'Channel'
    : conversation.metadata.channelType === 'dm'
      ? 'Direct message'
      : 'Group chat'

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-[2.5rem] border border-slate-800/60 bg-gradient-to-br p-8 text-slate-100 shadow-2xl',
        conversation.metadata.backgroundColor
      )}
    >
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-500">{conversation.metadata.workspaceName}</p>
          <div className="mt-2 flex items-center gap-3">
            <span className="text-xl font-semibold text-white">{conversation.metadata.channelName}</span>
            <span className={cn('rounded-full px-3 py-1 text-xs font-medium', theme.badge)}>
              {channelLabel}
            </span>
          </div>
          <p className="mt-2 text-sm text-slate-400">{conversation.metadata.channelTopic}</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" aria-hidden />
          {conversation.metadata.lastActivityLabel}
        </div>
      </div>
      <div className="space-y-6">
        {resolvedMessages.map(({ message, participant }) => (
          <MessagingMessage
            key={message.id}
            message={message}
            participant={participant}
            showAvatars={conversation.metadata.showAvatars}
            showTimestamps={conversation.metadata.showTimestamps}
            showReactions={conversation.metadata.showReactions}
          />
        ))}
      </div>
      {conversation.metadata.typingIndicator ? (
        <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-slate-700/70 px-4 py-2 text-xs text-slate-400">
          Someone is typing
          <span className="flex items-center gap-1">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-500" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-500" style={{ animationDelay: '0.1s' }} />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-500" style={{ animationDelay: '0.2s' }} />
          </span>
        </div>
      ) : null}
      <div className="mt-6 flex items-center justify-between border-t border-slate-800/70 pt-4 text-xs text-slate-400">
        <span>{conversation.metadata.workspaceName}</span>
        <span>{conversation.metadata.channelName}</span>
      </div>
    </div>
  )
}
